import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Typography,
  Grid,
  Avatar,
  useTheme,
  useMediaQuery,
  IconButton,
  InputAdornment,
  TextField,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import HighlightOffRoundedIcon from "@mui/icons-material/HighlightOffRounded";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import CardGlass from "./CardGlass";
import axios from "axios";
import { FILE_UPLOAD_URL } from "../../../env";
import {
  useInstagramInstructionDataQuery,
  useInstagramInstructionMutation,
  useUpdateInstagramInstructionMutation
} from "../../../Services/instagram/Instagram";
import { toast } from "react-toastify";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Tooltip from "@mui/material/Tooltip";
import SaveIcon from '@mui/icons-material/Save';
import { Button, CircularProgress } from "@mui/material";
import { USER_TEAM_ID } from "../../../constants";


const InstagramBotInstructions: React.FC<{ title: string }> = ({ title }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Ref for the hidden file input to reset its value
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Now this stores the S3 Key string, not full URL
  const [uploadedFileKey, setUploadedFileKey] = useState<string | null>(null);
  const [instagramList, setInstagramList] = useState<any[]>([]);
  const [selectedFileId, setSelectedFileId] = useState<number | null>(null);
  const [trainingDataFileName, setTrainingDataFileName] = useState("");
  const [fileLoading, setFileLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [doText, setDoText] = useState("");
  const [dontText, setDontText] = useState("");
  const [specText, setSpecText] = useState("");
  const [id, setID] = useState<any>(null)



  const {
    data: InstagramData,
    refetch: refetchInstagramData,
    isLoading,
  } = useInstagramInstructionDataQuery({ category: title.toLowerCase() });


  const [triggerInstagramInstructionMutation] = useInstagramInstructionMutation();
  const [triggerUpdateInstagramInstructionMutation] = useUpdateInstagramInstructionMutation();

  useEffect(() => {
    if (InstagramData?.results) {
      setInstagramList(InstagramData?.results[0]);
      setDoText(InstagramData?.results[0]?.do || "")
      setDontText(InstagramData?.results[0]?.dont || "")
      setSpecText(InstagramData?.results[0]?.spec || "")
      setTrainingDataFileName(InstagramData?.results[0]?.file_name || "")
      setID(InstagramData?.results[0]?.id ?? null)
    }
  }, [InstagramData]);


  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      // Only allow .txt files
      const fileName = file.name.toLowerCase()
      if (!(fileName.endsWith(".txt") || fileName.endsWith(".csv") || fileName.endsWith(".xlsx"))) {
        toast.error("Only .txt, .csv, .xlsx files are allowed.");
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        return;
      }
      try {
        const uploadResult = await imageUpload(file);
        // Show S3 Key instead of full URL
        setUploadedFileKey(uploadResult?.Key || null);
        setTrainingDataFileName(uploadResult?.url)

        console.log("File uploaded:", uploadResult);
        try {

          toast.success("File added successfully!");
          setFileLoading(false)

        } catch (error: any) {
          console.error("Add failed", error);
          toast.error(error?.data?.message || "Failed to add instruction.");
          setFileLoading(false)
        }
        // Reset file input after successful upload so user can re-upload same file if needed
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      } catch (error) {
        console.error("Upload failed:", error);
        toast.error("Failed to upload file.");
        setFileLoading(false)
      }
    }
  };

  const imageUpload = async (file: File) => {
    setFileLoading(true)
    try {
      const data = {
        files: [{ fileName: file.name, mimeType: file.type }],
      };
      // Expecting response.data to be an array of objects containing uploadURL, Key, url
      const response = await axios.post(FILE_UPLOAD_URL, data, {
        headers: { "Content-Type": "application/json" },
      });

      const { uploadURL, Key, url } = response.data[0];
      await axios.put(uploadURL, file, {
        headers: { "Content-Type": file.type },
      });

      return { Key, url };
    } catch (error) {
      setFileLoading(false)
      throw error;
    }
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);

      // Prepare the payload
      const payload1 = {
        do: doText,
        dont: dontText,
        spec: specText,
        file_name: trainingDataFileName,
        team: localStorage.getItem(USER_TEAM_ID) || null,
        category: title.toLowerCase()
      };
      const payload = {
        do: doText,
        dont: dontText,
        spec: specText,
        file_name: trainingDataFileName,
        team: localStorage.getItem(USER_TEAM_ID) || null,
        category: title.toLowerCase()
      };

      let result;

      if (id === null || id === undefined) {

        result = await triggerInstagramInstructionMutation({
          body: payload1
        }).unwrap();

      } else {
        // If teamNull is false and ID exists, update existing instruction
        result = await triggerUpdateInstagramInstructionMutation({
          body: payload,
          id: id
        }).unwrap();
      }

      // If we get here, the mutation was successful
      toast.success('Changes saved successfully!');

      // Refetch the latest data to update the UI
      await refetchInstagramData();

    } catch (error) {
      console.error('Error saving changes:', error);
      toast.error(error?.data?.message || 'Failed to save changes');
    } finally {
      setIsSaving(false);
    }
  };
  return (
    <Box
      sx={{
        height: "85vh",
        overflowY: "auto",
        background: "linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)",
        px: { xs: 3, md: 12 },
        py: { xs: 6, md: 10 },
        fontFamily: "'Inter', sans-serif",
        color: "text.primary",
      }}
    >
      <Box
        sx={{
          textAlign: "center",
          mb: { xs: 6, md: 8 },
          maxWidth: 680,
          mx: "auto",
        }}
      >
        <Avatar
          sx={{
            width: 84,
            height: 84,
            mx: "auto",
            mb: 3,
            bgcolor: "#3a4e78",
            boxShadow: "0 10px 25px rgba(58,78,120,0.25)",
          }}
        >
          <AutoAwesomeIcon sx={{ fontSize: 38, color: "#e3eafc" }} />
        </Avatar>
        <Typography
          variant={isMobile ? "h4" : "h3"}
          fontWeight={800}
          sx={{
            color: "#2a3751",
            letterSpacing: "-0.04em",
            mb: 1,
          }}
        >
          {title} Bot Instruction
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: 18,
            lineHeight: 1.6,
            maxWidth: 500,
            mx: "auto",
            color: "#4c5a80",
            fontWeight: 500,
            userSelect: "none",
          }}
        >
          Set your bot’s behavioral do's, don'ts, and technical specs with easy
          editing and smooth UI.
        </Typography>
      </Box>

      <Box sx={{ maxWidth: 480, mx: "auto", mb: 5 }}>
        <label className="fs-4 fw-bold d-flex align-items-center gap-2 mb-1">
          Training data
          {uploadedFileKey && (
            <Tooltip
              title="You can upload a new file only after deleting the current file."
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    fontSize: "1.2rem",
                  },
                },
              }}
            >
              <InfoOutlinedIcon
                color="info"
                sx={{ fontSize: 20, cursor: "pointer" }}
                aria-label="upload info"
              />
            </Tooltip>
          )}
        </label>

        <Box sx={{ display: "flex", alignItems: "center", gap: 2, width: '100%' }}>
          <Box sx={{ flex: 1 }}>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              value={trainingDataFileName || ""}
              placeholder="No file uploaded"
              InputProps={{
                readOnly: true,
                endAdornment: trainingDataFileName && (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => {
                        setUploadedFileKey(null);
                        setTrainingDataFileName("");

                        if (fileInputRef.current) {
                          fileInputRef.current.value = "";
                        }
                      }}
                      edge="end"
                      aria-label="clear uploaded file"
                    >
                      <DeleteOutlineIcon color="error" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <input
              type="file"
              accept=".txt"
              id="fileUpload"
              style={{ display: "none" }}
              onChange={handleFileChange}
              disabled={!!uploadedFileKey || !!trainingDataFileName}
              ref={fileInputRef}
            />
            <label
              htmlFor="fileUpload"
              style={{
                pointerEvents: (uploadedFileKey || trainingDataFileName) ? "none" : "auto",
              }}
            >
              <Box
                component="span"
                sx={{
                  px: 2,
                  py: 1,
                  bgcolor: (uploadedFileKey || trainingDataFileName) ? "#a9a9a9" : "#3a4e78",
                  color: "#fff",
                  borderRadius: 1,
                  fontWeight: 500,
                  cursor: (uploadedFileKey || trainingDataFileName || fileLoading) ? "not-allowed" : "pointer",
                  "&:hover": {
                    bgcolor: (uploadedFileKey || trainingDataFileName || fileLoading) ? "#a9a9a9" : "#2a3751",
                  },
                  userSelect: "none",
                  minWidth: 100,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '40px',
                }}
              >
                {fileLoading ? (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <svg style={{ marginRight: 6 }} width="18" height="18" viewBox="0 0 50 50">
                      <circle cx="25" cy="25" r="20" fill="none" stroke="#fff" strokeWidth="5" strokeDasharray="31.4 31.4" strokeLinecap="round">
                        <animateTransform attributeName="transform" type="rotate" from="0 25 25" to="360 25 25" dur="1s" repeatCount="indefinite" />
                      </circle>
                    </svg>
                    Loading
                  </span>
                ) : (
                  'Upload file'
                )}
              </Box>
            </label>

            {/* Save Button */}
            <Button
              variant="contained"
              size="medium"
              onClick={handleSave}
              disabled={!trainingDataFileName && !doText && !dontText && !specText || isSaving}
              sx={{
                height: '40px',
                fontWeight: 500,
                fontSize: '0.875rem',
                textTransform: 'none',
                backgroundColor: '#3a4e78',
                '&:hover': {
                  backgroundColor: '#2a3751',
                },
                '&:disabled': {
                  backgroundColor: '#e0e0e0',
                  color: '#9e9e9e',
                },
                minWidth: '120px',
              }}
              startIcon={isSaving ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <SaveIcon fontSize="small" />
              )}
            >
              {isSaving ? 'Saving...' : 'Save changes'}
            </Button>
          </Box>
        </Box>
      </Box>

      <Grid container spacing={4} justifyContent="center">
        <Grid item xs={12} sm={6} md={4}>
          <CardGlass
            title="Do's"
            Icon={CheckCircleRoundedIcon}
            value={doText}
            onChange={setDoText}
            color="#4caf50"
            placeholder="Enter what the bot should do..."
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <CardGlass
            title="Don'ts"
            Icon={HighlightOffRoundedIcon}
            value={dontText}
            onChange={setDontText}
            color="#f44336"
            placeholder="Enter what the bot should avoid..."
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <CardGlass
            title="Specifications"
            Icon={InfoRoundedIcon}
            value={specText}
            onChange={setSpecText}
            color="#6366f1"
            placeholder="Enter technical specifications..."
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default InstagramBotInstructions;
